import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { routes } from '@/lib/routes';
import { Plus, Home as HomeIcon, Phone, Video } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { CoachSessionSummary, SessionFormat } from '@/types/coach';
import { SessionFormModal } from './SessionFormModal';

interface Props {
  clientId: string;
  clientFirstName: string;
}

const PAGE_SIZE = 10;

const FORMAT_ICON: Record<SessionFormat, typeof HomeIcon> = {
  in_person: HomeIcon,
  phone: Phone,
  video: Video,
};

const FORMAT_LABEL: Record<SessionFormat, string> = {
  in_person: 'Persönlich',
  phone: 'Telefon',
  video: 'Video',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function SessionsList({ clientId, clientFirstName }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState<CoachSessionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  async function load(offset = 0) {
    try {
      const res = await api.coach.sessions.list(clientId, { limit: PAGE_SIZE, offset });
      if (offset === 0) {
        setItems(res.items);
      } else {
        setItems((prev) => [...prev, ...res.items]);
      }
      setTotal(res.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sessions konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const hasMore = items.length < total;

  if (loading) {
    return <p className="text-sm text-muted-foreground">Lade Sessions…</p>;
  }

  if (items.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus size={14} /> Neue Session
          </Button>
        </div>
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Noch keine Sessions. Lege die erste an, um den Fahrplan deiner Arbeit mit {clientFirstName} zu starten.
        </div>
        <SessionFormModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          clientId={clientId}
          onSaved={(saved) => {
            navigate(routes.coach.session(clientId, saved.id));
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? 'Session' : 'Sessions'}
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
          <Plus size={14} /> Neue Session
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((s) => {
          const Icon = FORMAT_ICON[s.format];
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => navigate(routes.coach.session(clientId, s.id))}
              className="block w-full text-left rounded-xl border border-border bg-card hover:bg-accent/50 transition-colors p-4"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>{formatDate(s.date)}</span>
                <span className="text-muted-foreground">·</span>
                <Icon size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">{FORMAT_LABEL[s.format]}</span>
                {s.durationMinutes !== null && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{s.durationMinutes} min</span>
                  </>
                )}
              </div>

              <p className="text-sm text-muted-foreground mt-2 line-clamp-2 min-h-[2.5em]">
                {s.keyInsightsPreview ?? (s.hasDebrief ? '—' : 'Noch nicht nachbereitet')}
              </p>

              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  s.hasDebrief
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {s.hasDebrief ? '✓ Nachbereitet' : '○ Offen'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  s.hasPlan
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {s.hasPlan ? '✓ Fahrplan' : '○ Kein Fahrplan'}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <div className="text-center pt-2">
          <Button variant="ghost" size="sm" onClick={() => load(items.length)}>
            Ältere Sessions anzeigen
          </Button>
        </div>
      )}

      <SessionFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        clientId={clientId}
        onSaved={(saved) => {
          navigate(routes.coach.session(clientId, saved.id));
        }}
      />
    </div>
  );
}
